import { InjectVuexModule, InjectModule } from 'vue-di/vuex';
import { Resolve } from 'vue-di';
import { EdgeEnrollmentApi, EdgeDocumentsApi, EdgeClaimApi, EdgeHubApi, EdgePayorApi, PayorV4Api , ApigatewayApi } from '@trialcard/apigateway.client';
import { Action } from 'vuex-module-decorators';
import moment from 'moment';
import { orderBy } from 'lodash';
import {
	ClaimDetailsResponseModel,
	MedicalClaimSearchModel,
	MedicalClaimSearchResponseModel,
	PharmacyClaimSearchResponseModel,
	ClaimGroupViewSearchModel,
	ParentLookupModel,
    GetBestEnrollmentV2RequestModel,
	ActualType,
} from '@trialcard/apigateway.models';

import { AxiosInstance, AxiosRequestConfig } from 'axios';
import { Gender, PayorType } from '@trialcard/enums';
import PrescriberModule from './prescriber';
import genders from '~/assets/genders';

@InjectModule({ stateFactory: true }, module)
export default class AccountModule extends InjectVuexModule {
	@Resolve
	prescriberModule!: PrescriberModule;
	@Resolve
	payorApi!: PayorV4Api;
	@Resolve
	edgePayor!: EdgePayorApi;
	@Resolve
	enrollmentApi!: EdgeEnrollmentApi;
	@Resolve
	documentApi!: EdgeDocumentsApi;
	@Resolve
	claimApi!: EdgeClaimApi;
	@Resolve
	edgeHub!: EdgeHubApi;

	rejectTypes!: ParentLookupModel[];
	
	@Resolve
    apiGateWay!: ApigatewayApi;

	@Action({})
	async getSiteAccountPrescribers() {
		return this.prescriberModule.getPrescribersAPI();
	}

	@Action({})
	async getPatientAccountPayor(payorId: number) {
		try {
			const getPayorResponse = await this.payorApi.payorGetByTenantId(payorId);
			return getPayorResponse.data && getPayorResponse.data.data;
		} catch {
			return;
		}
	}


	@Action({})
	async getEnrollment(payload: {patient: { firstName: string; lastName: string; dateOfBirth: string; zip: string; gender: string }, arrOp: boolean}) {
		try {
			const {patient, arrOp} = payload;
			let bestActiveEnrollment;
            const enrollmentRequestModel = {
                ...patient,
                dateOfBirth: moment.utc(patient.dateOfBirth),
                gender: Gender[patient.gender],
                includeQueuedTransient: true,
                includeRejected: true,
            } as GetBestEnrollmentV2RequestModel;
			const getBestEnrollmentResponse = await this.enrollmentApi.enrollmentGetBestEnrollmentsV2(enrollmentRequestModel);
			const enrollments = getBestEnrollmentResponse.data && getBestEnrollmentResponse.data.data;
			const activeEnrollments =
				enrollments &&
				enrollments.filter(e => {
					return moment(e.endDate!) > moment(Date.now());
				});
			if (activeEnrollments && activeEnrollments.length > 0) {
				bestActiveEnrollment = orderBy(activeEnrollments, enrl => moment(enrl.endDate).valueOf(), 'asc')[0];
			} else {
				bestActiveEnrollment = enrollments && orderBy(enrollments, enrl => moment(enrl.endDate).valueOf(), 'desc')[0];
			}

			if(arrOp){
				// Return all enrollments (not just active) so callers can select the
				// most-recent-startDate enrollment and derive status from its endDate.
				return enrollments;
			}else{
				return bestActiveEnrollment;
			}
		} catch {
			return;
		}
	}

	@Action({})
	async uploadDocument(request: { file: File; documentTypeId: string | null }) {
		try {
			const form = new FormData();
			if (request.documentTypeId) form.append('type', request.documentTypeId);
			form.append('attachment', request.file);
			const axios: AxiosInstance = (this.documentApi as any).axios;
			const axiosRequest: AxiosRequestConfig = {
				url: 'edge/documents/v1/Document/upload',
				method: 'POST',
				data: form,
				headers: {
					'x-program-id': process.env.programId!,
				},
			};
			await Promise.all([
				(this.documentApi as any).authentications.ApiKey.applyToRequest(axiosRequest),
				(this.documentApi as any).authentications.Bearer.applyToRequest(axiosRequest),
				(this.documentApi as any).authentications.Token.applyToRequest(axiosRequest),
			]);
			await axios.request(axiosRequest);
			return true;
		} catch {
			return false;
		}
	}

	@Action({})
	async getMedicalClaims(memberNumber: string) {
		try {
			const getClaimsResponse = await this.claimApi.claimMedicalClaimSearch({
				searchModel: {
					memberNumbers: [memberNumber],
				} as MedicalClaimSearchModel,
				filterByClaimGroup: true,
			});
			const claims = getClaimsResponse.data && getClaimsResponse.data.data;
			const response: MedicalClaimSearchResponseModel[] = claims || [];
			return response;
		} catch {
			return [];
		}
	}

	@Action({})
	async getPharmacyClaims(memberNumber: string) {
		try {
			const getClaimsResponse = await this.claimApi.claimPharmacyClaimSearch({
				searchModel: {
					memberNumbers: [memberNumber],
				} as MedicalClaimSearchModel,
				filterByClaimGroup: true,
			});
			const claims = getClaimsResponse.data && getClaimsResponse.data.data;
			const response: PharmacyClaimSearchResponseModel[] = claims || [];
			return response;
		} catch {
			return [];
		}
	}

	@Action({})
	async getGroupClaims(memberNumbers: string[]) {
		try {
			const response = await this.claimApi.claimGroupViewSearch({
				searchModel: {
					memberNumbers,
					includeAdjustments: true,
				} as ClaimGroupViewSearchModel,
				pageSize: 1000,
			});
			return response.data.data;
		} catch {
			return [];
		}
	}

    @Action({})
    async getPatientAccountPayorList(payorType = PayorType.INS) {
        try {
            const getPayorResponse = await this.payorApi.payorSearchByTenantId({ payorType, maxRecordCount: 100000 });
            return getPayorResponse.data && getPayorResponse.data.data;
        } catch {
            return;
        }
    }

	async getPharmacyInsurance(accountId: number) {
		try {
			const pharmacy = await this.edgePayor.coveragePharmacyCoverage({ accountId });

			let pharmacyInsurance;
			if (pharmacy.data && pharmacy.data.success && pharmacy.data.data) {
				pharmacyInsurance = pharmacy.data.data;
			}

			return pharmacyInsurance;
		} catch {
			return [];
		}
	}

	async getMedicalInsurance(accountId: number) {
		try {
			const medical = await this.edgePayor.coverageMedicalCoverage({ accountId });

			let medicalInsurance;
			if (medical.data && medical.data.success && medical.data.data) {
				medicalInsurance = medical.data.data;
			}

			return medicalInsurance;
		} catch {
			return [];
		}
	}

	@Action({})
	async getAccumulatorsBymMemberNumber(memberNumber: string) {
		try {
			const accumulatorResponse = await this.claimApi.claimAccumulatorSearch({
				memberNumbers: [memberNumber],
			});

			const accumulators = accumulatorResponse.data && accumulatorResponse.data.data;

			return accumulators || [];
		} catch (error) {
			return [];
		}
	}

	@Action({})
	async getAllRejectTypes() {
		try {
			const rejectTypesResponse = await this.claimApi.typesRejectTypes(' ');

			this.rejectTypes = rejectTypesResponse.data.data;

			return this.rejectTypes;
		} catch (error) {
			return [];
		}
	}

	getRejectReason(id: number) {
		if (this.rejectTypes && this.rejectTypes.length > 0) {
			return this.rejectTypes.find(item => item.id === id);
		} else {
			return undefined;
		}
	}
}
